import { logger, unionBy } from '@ninetailed/experience.js-shared';
import { NinetailedPlugin, OnChangeEmitter, isExperienceMatch, selectDistribution, PROFILE_CHANGE } from '@ninetailed/experience.js';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

const CONTAINER_WIDTH = 432;
const BUTTON_WIDTH = 48;
const BUTTON_HEIGHT = 192;
const BUTTON_BOTTOM_POSITION = 128;
const TRANSFORM_CLOSED = `translate(${CONTAINER_WIDTH - BUTTON_WIDTH}px, 0px)`;
const TRANSFORM_CLOSED_HIDE = `translate(${CONTAINER_WIDTH}px, 0px)`;
const TRANSFORM_OPEN = `translate(0px, 0px)`;
class WidgetContainer {
  constructor(options) {
    var _a, _b;
    this.options = options;
    this.container = document.createElement('div');
    this.container.style.position = 'fixed';
    this.container.style.zIndex = '999999';
    this.container.style.right = '0px';
    this.container.style.bottom = `${BUTTON_BOTTOM_POSITION}px`;
    this.container.style.width = `${CONTAINER_WIDTH}px`;
    this.container.style.height = `${BUTTON_HEIGHT}px`;
    if ((_b = (_a = options.ui) === null || _a === void 0 ? void 0 : _a.opener) === null || _b === void 0 ? void 0 : _b.hide) {
      this.container.style.transform = TRANSFORM_CLOSED_HIDE;
    } else {
      this.container.style.transform = TRANSFORM_CLOSED;
    }
    this.container.style.transitionTimingFunction = 'cubic-bezier(0.4, 0, 0.2, 1)';
    this.container.style.transitionDuration = '700ms';
    this.container.style.transitionProperty = 'transform';
    document.body.appendChild(this.container);
  }
  open() {
    this.container.style.transform = TRANSFORM_OPEN;
    this.container.style.height = '100vh';
    this.container.style.bottom = `0px`;
  }
  close() {
    var _a, _b;
    if ((_b = (_a = this.options.ui) === null || _a === void 0 ? void 0 : _a.opener) === null || _b === void 0 ? void 0 : _b.hide) {
      this.container.style.transform = TRANSFORM_CLOSED_HIDE;
    } else {
      this.container.style.transform = TRANSFORM_CLOSED;
    }
    setTimeout(() => {
      this.container.style.height = `${BUTTON_HEIGHT}px`;
      this.container.style.bottom = `${BUTTON_BOTTOM_POSITION}px`;
    }, 700);
  }
  get element() {
    return this.container;
  }
}

var _a;
class NinetailedPreviewPlugin extends NinetailedPlugin {
  constructor(options) {
    super();
    this.options = options;
    this.name = 'ninetailed:preview' + Math.random();
    this.isOpen = false;
    this.experiences = [];
    this.audienceDefinitions = [];
    this.audienceOverwrites = {};
    this.experienceVariantIndexOverwrites = {};
    this.profile = null;
    this.container = null;
    this.bridge = null;
    this.onChangeEmitter = new OnChangeEmitter();
    this.clientId = null;
    this.environment = null;
    this.initialize = () => __awaiter(this, void 0, void 0, function* () {
      var _b;
      if (typeof window !== 'undefined') {
        const {
          PreviewBridge
        } = yield import('@ninetailed/experience.js-preview-bridge');
        this.container = new WidgetContainer({
          ui: this.options.ui
        });
        this.bridge = PreviewBridge({
          url: this.options.url
        });
        this.bridge.render(this.container.element);
        window.ninetailed = Object.assign({}, window.ninetailed, {
          plugins: Object.assign(Object.assign({}, (_b = window.ninetailed) === null || _b === void 0 ? void 0 : _b.plugins), {
            preview: this.windowApi
          })
        });
        this.bridge.updateProps({
          props: this.pluginApi
        });
      }
    });
    this.loaded = () => true;
    this[_a] = ({
      payload
    }) => {
      if (payload === null || payload === void 0 ? void 0 : payload.profile) {
        this.onProfileChange(payload.profile);
      }
    };
    this.getExperienceSelectionMiddleware = ({
      baseline,
      experiences
    }) => {
      return () => {
        const experienceIds = Object.keys(this.pluginApi.experienceVariantIndexes);
        const experience = experiences.find(experience => {
          const hasActiveAudience = this.pluginApi.activeAudiences.some(activeAudienceId => {
            var _b;
            return ((_b = experience.audience) === null || _b === void 0 ? void 0 : _b.id) === activeAudienceId;
          });
          return hasActiveAudience && experienceIds.includes(experience.id);
        });
        if (!experience) {
          return {
            experience: null,
            variant: baseline,
            variantIndex: 0
          };
        }
        const baselineComponent = experience.components.find(component => component.baseline.id === baseline.id);
        if (!baselineComponent) {
          return {
            experience,
            variant: baseline,
            variantIndex: 0
          };
        }
        const allVariants = [baseline, ...baselineComponent.variants];
        const variantIndex = this.pluginApi.experienceVariantIndexes[experience.id];
        if (allVariants.length <= variantIndex) {
          return {
            experience,
            variant: baseline,
            variantIndex: 0
          };
        }
        const variant = allVariants[variantIndex];
        if (!variant) {
          return {
            experience,
            variant: baseline,
            variantIndex: 0
          };
        }
        return {
          experience,
          variant,
          variantIndex
        };
      };
    };
    this.onChange = () => {
      var _b;
      logger.debug('Ninetailed Preview Plugin onChange pluginApi:', this.pluginApi);
      Object.assign({}, window.ninetailed, {
        plugins: Object.assign(Object.assign({}, (_b = window.ninetailed) === null || _b === void 0 ? void 0 : _b.plugins), {
          preview: this.windowApi
        })
      });
      this.bridge.updateProps({
        props: this.pluginApi
      });
      this.onChangeEmitter.invokeListeners();
    };
    this.onProfileChange = profile => {
      this.profile = profile;
      this.onChange();
    };
    this.setCredentials = ({
      clientId,
      environment
    }) => {
      this.clientId = clientId;
      this.environment = environment;
    };
    this.experiences = options.experiences || [];
    this.audienceDefinitions = options.audiences || [];
    this.onOpenExperienceEditor = options.onOpenExperienceEditor;
    this.onOpenAudienceEditor = options.onOpenAudienceEditor;
  }
  open() {
    var _b;
    (_b = this.container) === null || _b === void 0 ? void 0 : _b.open();
    this.isOpen = true;
    this.onChange();
  }
  close() {
    var _b;
    (_b = this.container) === null || _b === void 0 ? void 0 : _b.close();
    setTimeout(() => {
      this.isOpen = false;
      this.onChange();
    }, 700);
  }
  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }
  activateAudience(id) {
    if (!this.isKnownAudience(id)) {
      console.log(`You cannot activate an unknown audience (id: ${id}).`);
      return;
    }
    this.audienceOverwrites = Object.assign(Object.assign({}, this.audienceOverwrites), {
      [id]: true
    });
    this.experienceVariantIndexOverwrites = Object.assign(Object.assign({}, this.experienceVariantIndexOverwrites), this.experiences.filter(experience => {
      var _b;
      return ((_b = experience.audience) === null || _b === void 0 ? void 0 : _b.id) === id;
    }).map(experience => experience.id).reduce((acc, curr) => {
      return Object.assign(Object.assign({}, acc), {
        [curr]: this.experienceVariantIndexes[curr] || 0
      });
    }, {}));
    this.onChange();
  }
  deactivateAudience(id) {
    if (!this.isKnownAudience(id)) {
      console.log(`You cannot deactivate an unkown audience (id: ${id}). How did you get it in the first place?`);
      return;
    }
    this.experienceVariantIndexOverwrites = Object.entries(this.experienceVariantIndexOverwrites).filter(([key, _]) => {
      return !this.experiences.filter(experience => {
        var _b;
        return ((_b = experience.audience) === null || _b === void 0 ? void 0 : _b.id) === id;
      }).map(experience => experience.id).includes(key);
    }).reduce((acc, [key, value]) => {
      return Object.assign(Object.assign({}, acc), {
        [key]: value
      });
    }, {});
    this.audienceOverwrites = Object.assign(Object.assign({}, this.audienceOverwrites), {
      [id]: false
    });
    this.onChange();
    this.experiences.filter(experience => {
      var _b;
      return ((_b = experience.audience) === null || _b === void 0 ? void 0 : _b.id) === id;
    }).forEach(experience => {
      this.setExperienceVariant({
        experienceId: experience.id,
        variantIndex: 0
      });
    });
    this.audienceOverwrites = Object.assign(Object.assign({}, this.audienceOverwrites), {
      [id]: false
    });
    this.onChange();
  }
  resetAudience(id) {
    if (!this.isKnownAudience(id)) {
      console.log(`You cannot reset an unknown audience (id: ${id}). How did you get it in the first place?`);
      return;
    }
    const _b = this.audienceOverwrites,
      _c = id;
      _b[_c];
      const audienceOverwrites = __rest(_b, [typeof _c === "symbol" ? _c : _c + ""]);
    this.audienceOverwrites = audienceOverwrites;
    this.onChange();
  }
  setExperienceVariant({
    experienceId,
    variantIndex
  }) {
    const experience = this.experiences.find(experience => experience.id === experienceId);
    if (!experience) {
      console.log(`You cannot active a variant for an unknown experience (id: ${experienceId})`);
      return;
    }
    if (experience.audience && !this.activeAudiences.some(id => {
      var _b;
      return id === ((_b = experience.audience) === null || _b === void 0 ? void 0 : _b.id);
    })) {
      console.log(`You cannot active a variant for an experience (id: ${experienceId}), which is not in the active audiences.`);
      return;
    }
    const isValidIndex = experience.components.map(component => component.variants.length + 1).every(length => length > variantIndex);
    if (!isValidIndex) {
      console.warn(`You activated a variant at index ${variantIndex} for the experience (id: ${experienceId}). Not all components have that many variants, you may see the baseline for some.`);
    }
    this.experienceVariantIndexOverwrites = Object.assign(Object.assign({}, this.experienceVariantIndexOverwrites), {
      [experienceId]: variantIndex
    });
    this.onChange();
  }
  resetExperience(experienceId) {
    const _b = this.experienceVariantIndexOverwrites,
      _c = experienceId;
      _b[_c];
      const experienceVariantIndexOverwrites = __rest(_b, [typeof _c === "symbol" ? _c : _c + ""]);
    this.experienceVariantIndexOverwrites = experienceVariantIndexOverwrites;
    this.onChange();
  }
  reset() {
    if (typeof window !== 'undefined' && window.ninetailed && typeof window.ninetailed.reset === 'function') {
      window.ninetailed.reset();
    }
  }
  openExperienceEditor(experience) {
    if (this.onOpenExperienceEditor && typeof this.onOpenExperienceEditor === 'function') return this.onOpenExperienceEditor(experience);
  }
  openExperienceAnalytics(experience) {
    window.open(`https://app.ninetailed.io/${this.clientId}/${this.environment}/experiences/${experience.id}`, '_blank');
  }
  openAudienceEditor(audience) {
    if (this.onOpenAudienceEditor && typeof this.onOpenAudienceEditor === 'function') return this.onOpenAudienceEditor(audience);
  }
  get pluginApi() {
    var _b;
    return {
      version: '2.0.0',
      open: this.open.bind(this),
      close: this.close.bind(this),
      toggle: this.toggle.bind(this),
      isOpen: this.isOpen,
      activateAudience: this.activateAudience.bind(this),
      deactivateAudience: this.deactivateAudience.bind(this),
      resetAudience: this.resetAudience.bind(this),
      apiAudiences: ((_b = this.profile) === null || _b === void 0 ? void 0 : _b.audiences) || [],
      audienceOverwrites: this.audienceOverwrites,
      activeAudiences: this.activeAudiences,
      audienceDefinitions: this.audienceDefinitions,
      setExperienceVariant: this.setExperienceVariant.bind(this),
      resetExperience: this.resetExperience.bind(this),
      apiExperienceVariantIndexes: this.apiExperienceVariantIndexes,
      experienceVariantIndexes: Object.assign(Object.assign({}, this.experienceVariantIndexes), this.experienceVariantIndexOverwrites),
      experienceVariantIndexOverwrites: this.experienceVariantIndexOverwrites,
      reset: this.reset.bind(this),
      experiences: this.experiences,
      openExperienceEditor: this.onOpenExperienceEditor ? this.openExperienceEditor.bind(this) : undefined,
      openExperienceAnalytics: this.openExperienceAnalytics.bind(this),
      openAudienceEditor: this.onOpenAudienceEditor ? this.openAudienceEditor.bind(this) : undefined
    };
  }
  get windowApi() {
    return {
      version: '2.0.0',
      open: this.open.bind(this),
      close: this.close.bind(this),
      toggle: this.toggle.bind(this),
      activateAudience: this.activateAudience.bind(this),
      deactivateAudience: this.deactivateAudience.bind(this),
      resetAudience: this.resetAudience.bind(this),
      activeAudiences: this.activeAudiences,
      setExperienceVariant: this.setExperienceVariant.bind(this),
      resetExperience: this.resetExperience.bind(this),
      experienceVariantIndexes: Object.assign(Object.assign({}, this.experienceVariantIndexes), this.experienceVariantIndexOverwrites)
    };
  }
  isKnownAudience(id) {
    return this.potentialAudiences.some(audience => audience.id === id);
  }
  get potentialAudiences() {
    const audiencesFromExperiences = this.experiences.map(experience => experience.audience).filter(audience => !!audience);
    return unionBy(this.audienceDefinitions, audiencesFromExperiences, 'id');
  }
  get activeAudiences() {
    var _b;
    const deactivatedAudiences = Object.entries(this.audienceOverwrites).filter(([id, active]) => !active).map(([id]) => id);
    const activatedAudiences = Object.entries(this.audienceOverwrites).filter(([id, active]) => active).map(([id]) => id);
    return [...(((_b = this.profile) === null || _b === void 0 ? void 0 : _b.audiences) || []), ...activatedAudiences].filter(id => !deactivatedAudiences.includes(id));
  }
  calculateExperienceVariantIndexes(profile) {
    const matchedExperiences = this.experiences.filter(experience => isExperienceMatch({
      experience,
      profile
    }));
    return matchedExperiences.reduce((acc, experience) => {
      const distribution = selectDistribution({
        experience,
        profile
      });
      return Object.assign(Object.assign({}, acc), {
        [experience.id]: distribution.index
      });
    }, {});
  }
  get apiExperienceVariantIndexes() {
    const profile = this.profile;
    if (!profile) {
      return {};
    }
    return this.calculateExperienceVariantIndexes(profile);
  }
  get experienceVariantIndexes() {
    const profile = this.profile;
    if (!profile) {
      return {};
    }
    return this.calculateExperienceVariantIndexes(Object.assign(Object.assign({}, profile), {
      audiences: this.activeAudiences
    }));
  }
}
_a = PROFILE_CHANGE;

if (typeof window === 'object' && !('process' in window)) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  window.process = {};
}

export { NinetailedPreviewPlugin, NinetailedPreviewPlugin as default };
